import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { resolveScannedQr } from "@/lib/resolveScannedQr";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Search, Filter, Download, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Share2, Eye, ScanLine, Mail, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StudentForm } from "@/components/forms/StudentForm";
import SignatureManager from "@/components/administration/SignatureManager";
import { StudentProfileDialog } from "@/components/administration/StudentProfileDialog";
import { BarcodeScannerDialog } from "@/components/administration/BarcodeScannerDialog";
import QrVisibilityManager from "@/components/administration/QrVisibilityManager";
import QrBypassRolesManager from "@/components/administration/QrBypassRolesManager";
import { AccountingDetailsDialog } from "@/components/administration/AccountingDetailsDialog";
import { AccountingBulkExcel } from "@/components/administration/AccountingBulkExcel";
import { ServerPagination } from "@/components/ui/server-pagination";
import { toast } from "@/hooks/use-toast";
import type { Tables } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { canManageModule } from "@/lib/permissions";
import { escapeHtml } from "@/lib/escapeHtml";
// xlsx loaded dynamically to reduce bundle size

type Student = Tables<"students">;

const STATUS_OPTIONS = [
  "registered_active", "registered_inactive", "unregistered_active", "unregistered_inactive",
  "postponed", "suspended", "discontinued", "deceased", "terminated", "active", "graduated",
];

const SIT_STATUS_OPTIONS = [
  { value: "first_sit", label: "First Sit" },
  { value: "repeating_module", label: "Repeat Module" },
  { value: "repeat_semester", label: "Repeat Semester" },
  { value: "supplementary", label: "Supplementary" },
];

const SEMESTER_OPTIONS = [
  { value: "Semester I", label: "Semester I" },
  { value: "Semester II", label: "Semester II" },
];

function normaliseClassCode(c: string | null | undefined): string {
  return (c ?? "").toUpperCase().replace(/\s+/g, "");
}

/** Generate plausible stored variants for a normalized class token (e.g. "PST4" -> ["PST4","PST 4"]). */
function classVariants(normalized: string): string[] {
  const m = normalized.match(/^([A-Z]+)(\d+)$/);
  if (!m) return [normalized];
  return [`${m[1]}${m[2]}`, `${m[1]} ${m[2]}`];
}

function getProgram(cls: string | null): string {
  if (!cls) return "—";
  const prefix = cls.toUpperCase();
  if (prefix.startsWith("CMT")) return "Clinical Medicine";
  if (prefix.startsWith("PST")) return "Pharmaceutical Sciences";
  if (prefix.startsWith("SW")) return "Social Work";
  return "—";
}

type SortKey = "student_number" | "name" | "class" | "status";
type SortDir = "asc" | "desc";

const Administration = () => {
  const { roles } = useAuth();
  const canManage = canManageModule(roles, "administration");
  const isSuperadmin = roles.includes("superadmin");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [profileStudent, setProfileStudent] = useState<Student | null>(null);
  const [accountingStudent, setAccountingStudent] = useState<Student | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterClasses, setFilterClasses] = useState<Set<string>>(new Set());
  const [filterStatuses, setFilterStatuses] = useState<Set<string>>(new Set());
  const [filterSitStatuses, setFilterSitStatuses] = useState<Set<string>>(new Set());
  const [filterSemesters, setFilterSemesters] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [scannerOpen, setScannerOpen] = useState(false);
  const queryClient = useQueryClient();

  const handleBarcodeScan = async (code: string) => {
    // The Administration global lookup accepts any of the 4 QR types.
    // Try each rotating-token type; first successful resolution wins.
    let resolvedCode: string | null = null;
    let lastReason: string | undefined;
    for (const t of ["admin", "academic", "examination", "accounting"] as const) {
      const r = await resolveScannedQr(code, t, "admin_lookup");
      if (r.ok) { resolvedCode = r.resolvedCode ?? code; break; }
      lastReason = r.message ?? r.reason;
      if (r.legacy) { resolvedCode = code; break; }
    }
    if (!resolvedCode) {
      toast({ title: "QR rejected", description: lastReason ?? "Invalid or expired QR code", variant: "destructive" });
      return;
    }
    const { data: student } = await supabase
      .from("students")
      .select("*")
      .or(`barcode_admin.eq.${resolvedCode},barcode_academic.eq.${resolvedCode},barcode_examination.eq.${resolvedCode},barcode_accounting.eq.${resolvedCode}`)
      .maybeSingle();

    if (student) {
      setProfileStudent(student);
      toast({ title: "Student found", description: `${student.first_name} ${student.last_name}` });
    } else {
      toast({ title: "Not found", description: `No student matches barcode "${code}"`, variant: "destructive" });
    }
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setCurrentPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset page on filter changes
  useEffect(() => { setCurrentPage(1); }, [filterClasses, filterStatuses, filterSitStatuses, filterSemesters, sortKey, sortDir]);

  // Server-side paginated query
  const { data, isLoading } = useQuery({
    queryKey: ["students", debouncedSearch, [...filterClasses], [...filterStatuses], [...filterSitStatuses], [...filterSemesters], sortKey, sortDir, currentPage, pageSize],
    queryFn: async () => {
      let q = supabase.from("students").select("*", { count: "exact" });

      // Server-side search
      if (debouncedSearch) {
        const s = `%${debouncedSearch}%`;
        q = q.or(`first_name.ilike.${s},last_name.ilike.${s},student_number.ilike.${s},class.ilike.${s}`);
      }

      // Server-side filters
      if (filterClasses.size > 0) {
        const variants = [...filterClasses].flatMap(classVariants);
        q = q.in("class", variants);
      }
      if (filterStatuses.size > 0) {
        q = q.in("status", [...filterStatuses]);
      }
      if (filterSitStatuses.size > 0) {
        q = q.in("sit_status", [...filterSitStatuses]);
      }
      if (filterSemesters.size > 0) {
        q = q.in("current_semester", [...filterSemesters]);
      }

      // Server-side sorting
      if (sortKey) {
        const col = sortKey === "name" ? "first_name" : sortKey;
        q = q.order(col, { ascending: sortDir === "asc" });
      } else {
        q = q.order("created_at", { ascending: false });
      }

      // Range
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;
      q = q.range(from, to);

      const { data, error, count } = await q;
      if (error) throw error;
      return { students: data as Student[], totalCount: count ?? 0 };
    },
  });

  const students = data?.students ?? [];
  const totalCount = data?.totalCount ?? 0;

  // Available classes for filter (separate lightweight query) — normalized to ignore spaces (e.g. "PST 4" === "PST4")
  const { data: availableClasses = [] } = useQuery({
    queryKey: ["student-classes"],
    queryFn: async () => {
      const { data } = await supabase.from("students").select("class");
      if (!data) return [];
      const norm = new Set<string>();
      for (const s of data) {
        const n = normaliseClassCode(s.class);
        if (n) norm.add(n);
      }
      return [...norm].sort();
    },
    staleTime: 60000,
  });

  // Fetch every student matching the current filters (ignoring pagination), in 1000-row chunks.
  const fetchAllFiltered = async (): Promise<Student[]> => {
    const all: Student[] = [];
    const CHUNK = 1000;
    for (let from = 0; ; from += CHUNK) {
      let q = supabase.from("students").select("*");
      if (debouncedSearch) {
        const s = `%${debouncedSearch}%`;
        q = q.or(`first_name.ilike.${s},last_name.ilike.${s},student_number.ilike.${s},class.ilike.${s}`);
      }
      if (filterClasses.size > 0) {
        const variants = [...filterClasses].flatMap(classVariants);
        q = q.in("class", variants);
      }
      if (filterStatuses.size > 0) {
        q = q.in("status", [...filterStatuses]);
      }
      if (filterSitStatuses.size > 0) {
        q = q.in("sit_status", [...filterSitStatuses]);
      }
      if (filterSemesters.size > 0) {
        q = q.in("current_semester", [...filterSemesters]);
      }
      q = q.order("created_at", { ascending: false }).range(from, from + CHUNK - 1);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data as Student[]) ?? [];
      all.push(...rows);
      if (rows.length < CHUNK) break;
    }
    return all;
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("students").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      toast({ title: "Student deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("students").delete().in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      setSelectedIds(new Set());
      toast({ title: `${selectedIds.size} students deleted` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
      const { error } = await supabase.from("students").update({ status }).in("id", ids);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      setSelectedIds(new Set());
      const label = vars.status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
      toast({ title: `${vars.ids.length} students updated to "${label}"` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const bulkFieldMutation = useMutation({
    mutationFn: async ({ ids, field, value }: { ids: string[]; field: "sit_status" | "current_semester"; value: string }) => {
      const payload: any = { [field]: value };
      const { error } = await supabase.from("students").update(payload).in("id", ids);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["students"] });
      setSelectedIds(new Set());
      const label = vars.field === "sit_status" ? "sit status" : "current semester";
      toast({ title: `${vars.ids.length} students: ${label} set to "${vars.value}"` });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown className="ml-1 h-3 w-3 inline" />;
    return sortDir === "asc" ? <ArrowUp className="ml-1 h-3 w-3 inline" /> : <ArrowDown className="ml-1 h-3 w-3 inline" />;
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pageIds = students.map(s => s.id);
    const allSelected = pageIds.every(id => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        pageIds.forEach(id => next.add(id));
        return next;
      });
    }
  };

  const toggleFilterClass = (cls: string) => {
    setFilterClasses(prev => {
      const next = new Set(prev);
      next.has(cls) ? next.delete(cls) : next.add(cls);
      return next;
    });
  };

  const toggleFilterStatus = (st: string) => {
    setFilterStatuses(prev => {
      const next = new Set(prev);
      next.has(st) ? next.delete(st) : next.add(st);
      return next;
    });
  };

  const toggleFilterSitStatus = (v: string) => {
    setFilterSitStatuses(prev => {
      const next = new Set(prev);
      next.has(v) ? next.delete(v) : next.add(v);
      return next;
    });
  };

  const toggleFilterSemester = (v: string) => {
    setFilterSemesters(prev => {
      const next = new Set(prev);
      next.has(v) ? next.delete(v) : next.add(v);
      return next;
    });
  };

  const handleEdit = (student: Student) => {
    setEditingStudent(student);
    setFormOpen(true);
  };

  const exportExcel = async (data: Student[]) => {
    const rows = data.map((s, i) => ({
      "#": i + 1,
      "Student #": s.student_number,
      "Name": `${s.first_name} ${s.last_name}`,
      "Class": s.class || "—",
      "Program": getProgram(s.class),
      "Status": s.status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
    }));
    const XLSX = await import("xlsx");
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, "students.xlsx");
    toast({ title: "Excel exported" });
  };

  const exportPdf = (data: Student[]) => {
    const header = ["#", "Student #", "Name", "Class", "Program", "Status"];
    const rows = data.map((s, i) => [
      i + 1, s.student_number, `${s.first_name} ${s.last_name}`,
      s.class || "—", getProgram(s.class),
      s.status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
    ]);
    const printWin = window.open("", "_blank");
    if (!printWin) return;
    printWin.document.write(`<html><head><title>Student Directory</title><style>
      body{font-family:Arial,sans-serif;padding:20px}
      table{width:100%;border-collapse:collapse}
      th,td{border:1px solid #ddd;padding:8px;text-align:left;font-size:13px}
      th{background:#f5f5f5;font-weight:bold}
      h1{font-size:18px}
    </style></head><body>
      <h1>Student Directory</h1>
      <table><thead><tr>${header.map(h => `<th>${escapeHtml(h)}</th>`).join("")}</tr></thead>
      <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>
    </body></html>`);
    printWin.document.close();
    printWin.print();
  };

  const shareWhatsApp = (data: Student[]) => {
    const summary = data.slice(0, 20).map((s, i) =>
      `${i + 1}. ${s.first_name} ${s.last_name} (${s.student_number}) - ${s.class || "N/A"}`
    ).join("\n");
    const text = `Student Directory (${data.length} students):\n\n${summary}${data.length > 20 ? `\n... and ${data.length - 20} more` : ""}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const exportData = selectedIds.size > 0
    ? students.filter(s => selectedIds.has(s.id))
    : students;

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold">Administration</h1>
            <p className="text-muted-foreground text-sm">Manage student records and institutional settings</p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/email-audit">
              <Mail className="h-4 w-4 mr-2" /> Email Audit
            </Link>
          </Button>
        </div>

        <Tabs defaultValue="students">
          <TabsList>
            <TabsTrigger value="students">Students</TabsTrigger>
            {isSuperadmin && <TabsTrigger value="qr-visibility">QR Visibility</TabsTrigger>}
            {isSuperadmin && <TabsTrigger value="qr-bypass">Scanner Bypass</TabsTrigger>}
            {isSuperadmin && <TabsTrigger value="signatures">Signatures</TabsTrigger>}
          </TabsList>

          <TabsContent value="students" className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search by name, ID, or class..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" className="sm:w-auto sm:px-3 relative">
                    <Filter className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Filter</span>
                    {(filterClasses.size > 0 || filterStatuses.size > 0 || filterSitStatuses.size > 0 || filterSemesters.size > 0) && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">
                        {filterClasses.size + filterStatuses.size + filterSitStatuses.size + filterSemesters.size}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 max-h-96 overflow-y-auto" align="end">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium mb-2">Class</p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {availableClasses.map(cls => (
                          <label key={cls} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={filterClasses.has(cls)} onCheckedChange={() => toggleFilterClass(cls)} />
                            {cls}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Status</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {STATUS_OPTIONS.map(st => (
                          <label key={st} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={filterStatuses.has(st)} onCheckedChange={() => toggleFilterStatus(st)} />
                            {st.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Sit Status</p>
                      <div className="space-y-1">
                        {SIT_STATUS_OPTIONS.map(opt => (
                          <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={filterSitStatuses.has(opt.value)} onCheckedChange={() => toggleFilterSitStatus(opt.value)} />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium mb-2">Current Semester</p>
                      <div className="space-y-1">
                        {SEMESTER_OPTIONS.map(opt => (
                          <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox checked={filterSemesters.has(opt.value)} onCheckedChange={() => toggleFilterSemester(opt.value)} />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    </div>
                    {(filterClasses.size > 0 || filterStatuses.size > 0 || filterSitStatuses.size > 0 || filterSemesters.size > 0) && (
                      <Button variant="ghost" size="sm" className="w-full" onClick={() => { setFilterClasses(new Set()); setFilterStatuses(new Set()); setFilterSitStatuses(new Set()); setFilterSemesters(new Set()); }}>
                        Clear Filters
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              <Button variant="outline" size="icon" className="sm:w-auto sm:px-3" onClick={() => setScannerOpen(true)}>
                <ScanLine className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Scan Barcode</span>
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="sm:w-auto sm:px-3">
                    <Download className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">Export</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuItem onClick={() => exportExcel(exportData)}>Export Excel</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportPdf(exportData)}>Export PDF</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => shareWhatsApp(exportData)}>
                    <Share2 className="mr-2 h-4 w-4" />Share via WhatsApp
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {canManage && (
                <AccountingBulkExcel
                  students={students as any}
                  totalFilteredCount={totalCount}
                  fetchAllFiltered={fetchAllFiltered as any}
                />
              )}
            </div>

            {selectedIds.size > 0 && canManage && (
              <div className="flex flex-wrap items-center gap-3 p-2 rounded-md bg-muted">
                <span className="text-sm font-medium">{selectedIds.size} selected</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm">Update Status</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="max-h-60 overflow-y-auto">
                    {STATUS_OPTIONS.map(st => (
                      <DropdownMenuItem
                        key={st}
                        onClick={() => bulkStatusMutation.mutate({ ids: [...selectedIds], status: st })}
                      >
                        {st.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm">Set Sit Status</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {SIT_STATUS_OPTIONS.map(opt => (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => bulkFieldMutation.mutate({ ids: [...selectedIds], field: "sit_status", value: opt.value })}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm">Set Semester</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {SEMESTER_OPTIONS.map(opt => (
                      <DropdownMenuItem
                        key={opt.value}
                        onClick={() => bulkFieldMutation.mutate({ ids: [...selectedIds], field: "current_semester", value: opt.value })}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                {isSuperadmin && (
                  <Button variant="destructive" size="sm" onClick={() => {
                    if (confirm(`Delete ${selectedIds.size} students?`)) {
                      bulkDeleteMutation.mutate([...selectedIds]);
                    }
                  }}>Delete Selected</Button>
                )}
                <Button variant="outline" size="sm" onClick={() => exportExcel(exportData)}>Export Selected</Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Clear</Button>
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Student Directory</CardTitle>
                <CardDescription>{totalCount} students found</CardDescription>
              </CardHeader>
              <CardContent className="table-responsive">
                {isLoading ? (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {canManage && (
                          <TableHead className="w-10">
                            <Checkbox
                              checked={students.length > 0 && students.every(s => selectedIds.has(s.id))}
                              onCheckedChange={toggleSelectAll}
                            />
                          </TableHead>
                        )}
                        <TableHead className="w-12">#</TableHead>
                        <TableHead className="cursor-pointer select-none" onClick={() => handleSort("student_number")}>
                          Student # <SortIcon col="student_number" />
                        </TableHead>
                        <TableHead className="cursor-pointer select-none" onClick={() => handleSort("name")}>
                          Name <SortIcon col="name" />
                        </TableHead>
                        <TableHead className="cursor-pointer select-none" onClick={() => handleSort("class")}>
                          Class <SortIcon col="class" />
                        </TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead className="cursor-pointer select-none" onClick={() => handleSort("status")}>
                          Status <SortIcon col="status" />
                        </TableHead>
                        <TableHead>Profile</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {students.length === 0 ? (
                        <TableRow><TableCell colSpan={canManage ? 9 : 8} className="text-center text-muted-foreground py-8">No students found</TableCell></TableRow>
                      ) : (
                        students.map((s, idx) => (
                          <TableRow key={s.id} className={selectedIds.has(s.id) ? "bg-muted/50" : ""}>
                            {canManage && (
                              <TableCell>
                                <Checkbox checked={selectedIds.has(s.id)} onCheckedChange={() => toggleSelect(s.id)} />
                              </TableCell>
                            )}
                            <TableCell className="text-muted-foreground text-sm">{(currentPage - 1) * pageSize + idx + 1}</TableCell>
                            <TableCell className="font-mono text-sm">{s.student_number}</TableCell>
                            <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                            <TableCell>{s.class || "—"}</TableCell>
                            <TableCell className="text-sm">{getProgram(s.class)}</TableCell>
                            <TableCell><StatusBadge status={s.status} /></TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => setProfileStudent(s)} title="View Profile">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                            <TableCell>
                              {canManage && (
                                <div className="flex gap-1">
                                  <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} title="Add Student Details"><Pencil className="h-4 w-4" /></Button>
                                  <Button variant="ghost" size="sm" onClick={() => setAccountingStudent(s)} title="Add Accounting Details"><Wallet className="h-4 w-4" /></Button>
                                  {isSuperadmin && (
                                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(s.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                  )}
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}

                <ServerPagination
                  currentPage={currentPage}
                  pageSize={pageSize}
                  totalCount={totalCount}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {isSuperadmin && (
            <TabsContent value="qr-visibility">
              <QrVisibilityManager />
            </TabsContent>
          )}
          {isSuperadmin && (
            <TabsContent value="qr-bypass">
              <QrBypassRolesManager />
            </TabsContent>
          )}
          {isSuperadmin && (
            <TabsContent value="signatures">
              <SignatureManager />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <StudentForm open={formOpen} onOpenChange={setFormOpen} student={editingStudent} onSuccess={() => queryClient.invalidateQueries({ queryKey: ["students"] })} />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Student</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. This will permanently delete the student record.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteId) { deleteMutation.mutate(deleteId); setDeleteId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <StudentProfileDialog open={!!profileStudent} onOpenChange={() => setProfileStudent(null)} student={profileStudent} />
      <AccountingDetailsDialog open={!!accountingStudent} onOpenChange={(o) => !o && setAccountingStudent(null)} student={accountingStudent as any} />
      <BarcodeScannerDialog open={scannerOpen} onOpenChange={setScannerOpen} onScan={handleBarcodeScan} />
    </AppLayout>
  );
};

export default Administration;
